import { defineMessages } from "react-intl";

import {
	bilirubinMessages,
	bloodMessages,
	glucoseMessages,
	ketonMessages,
	proteinMessages,
	urobilinogenMessages,
	validator
} from "lib/build/forms/ktx/transplantation-screening/lab-procedures/totalUrineAnalysis";

import { FieldData, FieldListData, FieldsetData } from "src/features/forms";
import { genericErrorMessages } from "src/messages/generic";
import { LeafNode } from "src/modules/shared/form-explorer";

const messages = defineMessages({
	totalUrineAnalysis: {
		id: "ktx.form.total_urine_analysis.title",
		defaultMessage: "Teljes vizelet"
	},
	pH: {
		id: "ktx.form.total_urine_analysis.ph",
		defaultMessage: "pH"
	},
	gravity: {
		id: "ktx.form.total_urine_analysis.gravity",
		defaultMessage: "Fajsúly"
	},
	glucose: {
		id: "ktx.form.total_urine_analysis.glucose",
		defaultMessage: "Glükóz"
	},
	protein: {
		id: "ktx.form.total_urine_analysis.protein",
		defaultMessage: "Fehérje"
	},
	blood: {
		id: "ktx.form.total_urine_analysis.blood",
		defaultMessage: "Vér"
	},
	bilirubin: {
		id: "ktx.form.total_urine_analysis.bilirubin",
		defaultMessage: "Bilirubin"
	},
	urobilinogen: {
		id: "ktx.form.total_urine_analysis.urobilinogen",
		defaultMessage: "Urobilinogen"
	},
	keton: {
		id: "ktx.form.total_urine_analysis.keton",
		defaultMessage: "Keton"
	},
	RBC: {
		id: "ktx.form.total_urine_analysis.rbc",
		defaultMessage: "VVT"
	},
	WBC: {
		id: "ktx.form.total_urine_analysis.wbc",
		defaultMessage: "FVS"
	},
	SC: {
		id: "ktx.form.total_urine_analysis.sc",
		defaultMessage: "LHS"
	},
	bacteria: {
		id: "ktx.form.total_urine_analysis.bacteria",
		defaultMessage: "Baktérium"
	},
	fungus: {
		id: "ktx.form.total_urine_analysis.fungus",
		defaultMessage: "Gomba"
	},
	other: {
		id: "ktx.form.total_urine_analysis.other",
		defaultMessage: "Egyéb"
	}
});

const fields: { [key: string]: FieldData | FieldListData } = {
	pH: {
		type: "field",
		path: ["pH"],
		label: messages.pH,
		errorMessages: genericErrorMessages,
		input: { type: "input", props: { type: "number" } }
	},
	gravity: {
		type: "field",
		path: ["gravity"],
		label: messages.gravity,
		errorMessages: genericErrorMessages,
		input: { type: "input", props: { type: "number" } }
	},
	glucose: {
		type: "field",
		path: ["glucose"],
		label: messages.glucose,
		errorMessages: genericErrorMessages,
		input: { type: "button-group", props: { labels: glucoseMessages } }
	},
	protein: {
		type: "field",
		path: ["protein"],
		label: messages.protein,
		errorMessages: genericErrorMessages,
		input: { type: "button-group", props: { labels: proteinMessages } }
	},
	blood: {
		type: "field",
		path: ["blood"],
		label: messages.blood,
		errorMessages: genericErrorMessages,
		input: { type: "button-group", props: { labels: bloodMessages } }
	},
	bilirubin: {
		type: "field",
		path: ["bilirubin"],
		label: messages.bilirubin,
		errorMessages: genericErrorMessages,
		input: { type: "button-group", props: { labels: bilirubinMessages } }
	},
	urobilinogen: {
		type: "field",
		path: ["urobilinogen"],
		label: messages.urobilinogen,
		errorMessages: genericErrorMessages,
		input: { type: "button-group", props: { labels: urobilinogenMessages } }
	},
	keton: {
		type: "field",
		path: ["keton"],
		label: messages.keton,
		errorMessages: genericErrorMessages,
		input: { type: "button-group", props: { labels: ketonMessages } }
	},
	RBC: {
		type: "field",
		path: ["RBC"],
		label: messages.RBC,
		errorMessages: genericErrorMessages,
		input: { type: "input", props: { type: "number" } }
	},
	WBC: {
		type: "field",
		path: ["WBC"],
		label: messages.WBC,
		errorMessages: genericErrorMessages,
		input: { type: "input", props: { type: "number" } }
	},
	SC: {
		type: "field",
		path: ["SC"],
		label: messages.SC,
		errorMessages: genericErrorMessages,
		input: { type: "input", props: { type: "number" } }
	},
	bacteria: {
		type: "field",
		path: ["bacteria"],
		label: messages.bacteria,
		errorMessages: genericErrorMessages,
		input: { type: "input", props: { type: "number" } }
	},
	fungus: {
		type: "field",
		path: ["fungus"],
		label: messages.fungus,
		errorMessages: genericErrorMessages,
		input: { type: "input", props: { type: "number" } }
	},
	other: {
		type: "field",
		path: ["other"],
		label: messages.other,
		errorMessages: genericErrorMessages,
		input: { type: "textarea", props: {} }
	}
};

const fieldset: FieldsetData = {
	rows: [
		{ columns: [{ width: 12, field: fields.pH }] },
		{ columns: [{ width: 12, field: fields.gravity }] },
		{ columns: [{ width: 12, field: fields.glucose }] },
		{ columns: [{ width: 12, field: fields.protein }] },
		{ columns: [{ width: 12, field: fields.blood }] },
		{ columns: [{ width: 12, field: fields.bilirubin }] },
		{ columns: [{ width: 12, field: fields.urobilinogen }] },
		{ columns: [{ width: 12, field: fields.keton }] },
		{ columns: [{ width: 12, field: fields.RBC }] },
		{ columns: [{ width: 12, field: fields.WBC }] },
		{ columns: [{ width: 12, field: fields.SC }] },
		{ columns: [{ width: 12, field: fields.bacteria }] },
		{ columns: [{ width: 12, field: fields.fungus }] },
		{ columns: [{ width: 12, field: fields.other }] }
	]
};

export const node: LeafNode = {
	id: "ktx_totalUrineAnalysis",
	value: {
		schemaId: "totalUrineAnalysis",
		title: messages.totalUrineAnalysis,
		validator,
		fieldset,
		applyCurrentDate: false
	}
};
